import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useDriver } from '../context/DriverContext';
import { useLanguage } from '../i18n/LanguageContext';
import { APP } from '../config/app';
import LangToggle from '../components/LangToggle';
import BottomNav from '../components/BottomNav';
import NotificationPrompt from '../components/NotificationPrompt';
import BatteryOptimizationNotice from '../components/BatteryOptimizationNotice';
import FirstTimeTour from '../components/FirstTimeTour';
import IntertownTripCard from '../components/IntertownTripCard';
import OpenIntertownOffers from '../components/OpenIntertownOffers';
import { listenToSearchingRides, acceptRide } from '../services/firebase';
import {
  createMap, getCurrentPosition, placeSelfMarker, updateSelfMarker,
  drawRoute, placeMarker, fitMapToPoints, clearMarkers,
} from '../services/maps';
import { playAlertSound, unlockAudio, speakRideAnnouncement, startContinuousAlert, stopContinuousAlert } from '../utils/alertSound';
import { listenForegroundMessages, clearRideNotification } from '../services/notifications';
import { executeRideAccept } from '../utils/rideFlowHelpers';
import { requestQueuedPlayReview } from '../utils/playReview';
import {
  ensureLocationPermission, showLocationDeniedToast, showLocationDisabledToast,
} from '../utils/locationPermission';
import { hapticSelection } from '../utils/haptics';

const ACCEPT_SECONDS = 25;

// A booking can be re-offered after a different Anna cancels it. The ride id
// remains stable for passenger history, so driver-only transient UI state must
// also include the server-owned offer revision. Legacy open rides map to 1.
const offerRevisionOf = (ride) => Math.max(1, Number(ride?.offerRevision) || 1);
const offerKeyOf = (rideId, offerRevision = 1) => `${rideId}:${Math.max(1, Number(offerRevision) || 1)}`;
const offerKeyForRide = (ride) => offerKeyOf(ride?.id, offerRevisionOf(ride));

export default function Dashboard({ isTabActive = true }) {
  const navigate = useNavigate();
  const {
    driver, activeRide, toggleOnline, user, locationBlocked, myPos,
    driverActionsReady, profileStatus, setRideOfferOpen,
  } = useDriver();
  const { t, lang } = useLanguage();
  const liveOnline = Boolean(driverActionsReady && driver?.isOnline);
  const dispatchHeld = String(driver?.adminDispatchHold?.state || '') === 'after_trip';

  // "Account under review" is a definitive state — only ever show it once the
  // profile has actually resolved from the server. While auth/profile are still
  // being restored on a cold start, show the dashboard optimistically (matching
  // App.js) rather than flashing the review screen at an already-approved Anna.
  // A settled answer — profileStatus 'ready' / 'offline-cached' / 'profile-error',
  // or driver.isBlocked — always wins and shows the correct screen.
  const profileResolving = profileStatus === 'restoring-auth' || profileStatus === 'refreshing-profile';
  const treatApproved = driver?.isApproved === true || (profileResolving && !driver?.isBlocked);
  const [requests, setRequests] = useState([]);

  // A required update never covers a live offer: the current acceptance window
  // remains entirely owned by the dispatch flow.
  useEffect(() => {
    setRideOfferOpen?.(requests.length > 0);
    return () => setRideOfferOpen?.(false);
  }, [requests.length, setRideOfferOpen]);
  const [dismissed, setDismissed] = useState({});
  const [accepting, setAccepting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(ACCEPT_SECONDS);
  const [onlineNet, setOnlineNet] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [showTour, setShowTour] = useState(() => localStorage.getItem('mt_driver_tour_done') !== '1');
  const [mapReady, setMapReady] = useState(false);
  const myPosRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const mapViewportSizeRef = useRef({ width: 0, height: 0 });
  const selfMarker = useRef(null);
  const alertedRef = useRef({});
  const previewRouteRef = useRef(null);
  const previewMarkersRef = useRef([]);
  const acceptingRef = useRef(false);
  // The same ride id can be offered again after another Anna cancels. Keep the
  // currently visible server offer identity in a ref so a delayed old FCM
  // cleanup cannot clear the newer card or its notification.
  const activeOfferKeyRef = useRef(null);

  const ensureSelfMarker = async (pos, { pan = false } = {}) => {
    if (!pos?.lat || !pos?.lng) return;
    myPosRef.current = pos;
    const map = mapInstance.current;
    if (!map) return;
    try {
      if (selfMarker.current) updateSelfMarker(selfMarker.current, pos);
      else selfMarker.current = await placeSelfMarker(map, pos);
      if (pan) {
        map.panTo(pos);
        if (map.getZoom() < 15) map.setZoom(16);
      }
    } catch (e) {
      console.warn('self marker failed', e);
    }
  };

  useEffect(() => {
    if (activeRide) navigate(`/trip/${activeRide.id}`, { replace: true });
  }, [activeRide, navigate]);

  // Do not interrupt a trip or an inactive retained tab. A completed trip
  // queues this check, then the driver profile's server-owned lifetime count
  // confirms the milestone once it reaches the idle dashboard.
  useEffect(() => {
    const completedRides = Number(driver?.totalCompletedRides);
    if (!isTabActive || activeRide || !user?.uid || !Number.isFinite(completedRides)) return undefined;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      const result = await requestQueuedPlayReview({ uid: user.uid, completedRides });
      if (!cancelled && result.state === 'waiting') {
        // Keep the pending record. DriverContext's profile listener will
        // re-run this effect as soon as the backend completion aggregate lands.
      }
    }, 1800);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [isTabActive, activeRide, user?.uid, driver?.totalCompletedRides]);

  useEffect(() => {
    const on = () => setOnlineNet(true);
    const off = () => setOnlineNet(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  useEffect(() => {
    if (!driverActionsReady || !driver?.isOnline || !driver?.isApproved || driver?.isBlocked || dispatchHeld) {
      setRequests([]);
      return undefined;
    }
    return listenToSearchingRides(user?.uid, setRequests);
  }, [driverActionsReady, driver?.isOnline, driver?.isApproved, driver?.isBlocked, dispatchHeld, user?.uid]);

  const lastFareRef = useRef({});

  // Reset dismissal and re-alert driver if a searching ride had its fare boosted!
  useEffect(() => {
    if (!requests?.length) return;
    requests.forEach((r) => {
      const offerKey = offerKeyForRide(r);
      const prevFare = lastFareRef.current[offerKey];
      const currentFare = Number(r.fare || 0);
      const isBoosted = Number(r.tip || r.fareTip || 0) > 0;
      if (prevFare != null && currentFare > prevFare && isBoosted) {
        setDismissed((d) => ({ ...d, [offerKey]: false }));
        if (alertedRef.current) {
          delete alertedRef.current[offerKey];
        }
      }
      lastFareRef.current[offerKey] = currentFare;
    });
  }, [requests]);

  useEffect(() => {
    if (!user?.uid) return undefined;
    return listenForegroundMessages((payload) => {
      const data = payload?.data || {};
      if (data.type === 'ride_gone' || data.type === 'ride_taken' || data.clearRequest === '1') {
        const rideId = data.rideId;
        if (rideId) {
          // An old clear must never suppress a newer rematch of the same
          // booking. FCM is only a wake/cleanup path; Firestore remains the
          // source of truth for the current targeted offer.
          const offerKey = offerKeyOf(rideId, data.offerRevision);
          setDismissed((d) => ({ ...d, [offerKey]: true }));
          // A clear for revision 1 must not silence or remove a revision 2
          // rematch of the same ride. If no newer offer is visible, normal
          // cleanup still happens.
          if (!activeOfferKeyRef.current || activeOfferKeyRef.current === offerKey) {
            stopContinuousAlert();
            clearRideNotification(rideId);
          }
        }
        return;
      }
      // A real request that arrived while the app is open — the accept card
      // already shows it in full, so no toast (and no duplicate) on top.
      if (data.type === 'ride') return;
      const title = payload?.notification?.title || t('dash.newRide');
      const body = payload?.notification?.body || '';
      toast(`${title}${body ? ` — ${body}` : ''}`);
    });
  }, [user?.uid, t]);

  // Safety net for #2: when a ride we were shown stops searching (taken / cancelled
  // / expired), clear its tray notification even if the ride_gone push never landed.
  const prevRequestIdsRef = useRef([]);
  useEffect(() => {
    const nowIds = (requests || []).map((r) => r.id);
    prevRequestIdsRef.current.forEach((id) => {
      if (!nowIds.includes(id)) clearRideNotification(id);
    });
    prevRequestIdsRef.current = nowIds;
  }, [requests]);

  useEffect(() => {
    // Auth/profile verification wins the startup network budget. Maps begins
    // only after that secure bootstrap has completed.
    if (!driverActionsReady || !driver?.isApproved || driver?.isBlocked) return undefined;
    let cancelled = false;
    (async () => {
      await new Promise((r) => requestAnimationFrame(() => r()));
      if (cancelled || !mapRef.current || mapInstance.current) return;
      try {
        const initialCenter = myPos?.lat ? myPos : APP.mapCenter;
        const map = await createMap(mapRef.current, initialCenter);
        if (cancelled) return;
        mapInstance.current = map;
        setMapReady(true);
        mapViewportSizeRef.current = {
          width: mapRef.current?.clientWidth || 0,
          height: mapRef.current?.clientHeight || 0,
        };
        if (myPos?.lat) {
          ensureSelfMarker(myPos, { pan: false });
        } else {
          getCurrentPosition({ timeout: 4000, maximumAge: 10000 })
            .then((pos) => { if (!cancelled) ensureSelfMarker(pos, { pan: true }); })
            .catch(() => {});
        }
      } catch {
        if (!cancelled) toast.error(t('dash.mapsError'));
      }
    })();
    return () => { cancelled = true; };
  }, [driverActionsReady, driver?.isApproved, driver?.isBlocked, t]);

  // The Dashboard stays mounted behind Earnings/Profile. Repaint only when
  // its viewport really changed (rotation/resume), not on every tab switch.
  useEffect(() => {
    if (!isTabActive || !mapInstance.current || !mapRef.current) return undefined;
    let secondFrame = null;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        const map = mapInstance.current;
        const container = mapRef.current;
        if (!map || !container) return;
        const nextSize = { width: container.clientWidth, height: container.clientHeight };
        const previous = mapViewportSizeRef.current;
        if (nextSize.width === previous.width && nextSize.height === previous.height) return;
        const center = map.getCenter?.();
        const zoom = map.getZoom?.();
        window.google?.maps?.event?.trigger(map, 'resize');
        if (center) map.setCenter(center);
        if (Number.isFinite(zoom)) map.setZoom(zoom);
        mapViewportSizeRef.current = nextSize;
      });
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      if (secondFrame) cancelAnimationFrame(secondFrame);
    };
  }, [isTabActive, mapReady]);

  // After toggling online, resize + place / refresh self marker.
  useEffect(() => {
    if (!liveOnline || !mapInstance.current) return undefined;
    const map = mapInstance.current;
    const id = requestAnimationFrame(() => {
      try {
        window.google?.maps?.event?.trigger(map, 'resize');
      } catch { /* */ }
      getCurrentPosition()
        .then((pos) => ensureSelfMarker(pos, { pan: true }))
        .catch(() => {
          map.panTo(APP.mapCenter);
          map.setZoom(14);
        });
    });
    return () => cancelAnimationFrame(id);
  }, [liveOnline]);

  // The home-map dot follows the shared, throttled position from DriverContext
  // (single GPS watcher for the whole app — no duplicate watch here).
  useEffect(() => {
    if (!driver?.isApproved || driver?.isBlocked || !myPos?.lat) return;
    myPosRef.current = myPos;
    ensureSelfMarker(myPos, { pan: !selfMarker.current });
  }, [myPos, driver?.isApproved, driver?.isBlocked]);

  const incoming = useMemo(() => requests.find((r) => {
    if (dismissed[offerKeyForRide(r)]) return false;
    if (Array.isArray(r.cancelledDriverIds) && r.cancelledDriverIds.includes(user?.uid)) return false;
    // The dispatch service is authoritative for ring distance and availability.
    // Never broaden an offer locally when GPS is missing or stale: this keeps
    // far Annas from seeing, hearing, or racing for an unassigned request.
    return Array.isArray(r.notifiedDriverIds) && r.notifiedDriverIds.includes(user?.uid);
  }) || null, [requests, dismissed, user?.uid, myPos]);
  const incomingOfferKey = incoming ? offerKeyForRide(incoming) : null;

  useEffect(() => {
    activeOfferKeyRef.current = incomingOfferKey;
  }, [incomingOfferKey]);

  useEffect(() => {
    if (!incoming?.id) {
      setSecondsLeft(ACCEPT_SECONDS);
      stopContinuousAlert();
      return undefined;
    }
    if (!alertedRef.current[incomingOfferKey]) {
      alertedRef.current[incomingOfferKey] = true;
      // Step 1: Immediate attention chime
      playAlertSound();
      // Step 2: Speak announcement clearly
      speakRideAnnouncement({
        pickup: incoming.pickup?.address || incoming.pickup?.name || '',
        fare: incoming.fare || 0,
        lang,
      });
      // Step 3: Start continuous alert loop after speech begins
      const loopTimeout = setTimeout(() => {
        if (incoming?.id && !acceptingRef.current) {
          startContinuousAlert();
        }
      }, 1200);
      alertedRef.current[`${incomingOfferKey}_loop`] = loopTimeout;
    }
    setSecondsLeft(ACCEPT_SECONDS);
    const tick = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(tick);
          stopContinuousAlert();
          setDismissed((d) => ({ ...d, [incomingOfferKey]: true }));
          return ACCEPT_SECONDS;
        }
        return s - 1;
      });
    }, 1000);
    return () => {
      clearInterval(tick);
      stopContinuousAlert();
      if (alertedRef.current[`${incomingOfferKey}_loop`]) {
        clearTimeout(alertedRef.current[`${incomingOfferKey}_loop`]);
      }
    };
  }, [incomingOfferKey, lang]);

  const clearPreviewRoute = () => {
    if (previewRouteRef.current) {
      try { previewRouteRef.current.setMap(null); } catch { /* */ }
      previewRouteRef.current = null;
    }
    clearMarkers(previewMarkersRef.current);
  };

  // Draw Route Polyline & Pins (A=Pickup, B=Drop) on map during incoming ride request (Draw ONCE per ride, no GPS flickering)
  useEffect(() => {
    const map = mapInstance.current;
    if (!map || !incoming || !incoming.pickup?.lat) {
      clearPreviewRoute();
      const currentPos = myPosRef.current;
      if (currentPos && map && liveOnline && !incoming) {
        map.panTo(currentPos);
      }
      return undefined;
    }

    let cancelled = false;
    (async () => {
      clearPreviewRoute();
      const pointsToFit = [];
      const currentPos = myPosRef.current;
      if (currentPos?.lat) pointsToFit.push(currentPos);

      // Place Pickup Marker (Pin A)
      if (incoming.pickup?.lat && incoming.pickup?.lng) {
        const pickupPos = { lat: incoming.pickup.lat, lng: incoming.pickup.lng };
        pointsToFit.push(pickupPos);
        const mPickup = await placeMarker(map, pickupPos, 'A', '#00d68f');
        if (!cancelled && mPickup) previewMarkersRef.current.push(mPickup);
      }

      // Place Drop Marker (Pin B)
      if (incoming.destination?.lat && incoming.destination?.lng) {
        const dropPos = { lat: incoming.destination.lat, lng: incoming.destination.lng };
        pointsToFit.push(dropPos);
        const mDrop = await placeMarker(map, dropPos, 'B', '#ef4444');
        if (!cancelled && mDrop) previewMarkersRef.current.push(mDrop);
      }

      // Straight pickup -> drop line on the request card. The real routed
      // polyline is drawn on /trip after the Anna accepts (ActiveTrip already
      // calls getDirections there). A paid Directions request per offer
      // received — several per accepted ride — was the biggest avoidable
      // Directions cost and added nothing to the accept/decline decision.
      if (incoming.pickup?.lat && incoming.destination?.lat) {
        try {
          const line = await drawRoute(map, [
            { lat: incoming.pickup.lat, lng: incoming.pickup.lng },
            { lat: incoming.destination.lat, lng: incoming.destination.lng },
          ]);
          if (!cancelled) previewRouteRef.current = line;
        } catch (e) {
          console.warn('Could not draw route preview on incoming request:', e);
        }
      }

      // Fit map to show Anna, Pickup, and Destination with bottom sheet padding (once per ride)
      if (!cancelled && pointsToFit.length) {
        fitMapToPoints(map, pointsToFit, { top: 90, bottom: 350, left: 40, right: 40 });
      }
    })();

    return () => {
      cancelled = true;
      clearPreviewRoute();
    };
  // `incoming` can arrive while the async Maps SDK/map instance is still
  // starting. Re-run once that instance is ready so the existing local
  // A/B markers and preview line are not lost on a cold-start request.
  }, [incoming?.id, liveOnline, mapReady]);

  const handleToggleOnline = async () => {
    if (!driverActionsReady) return;
    hapticSelection();
    unlockAudio();
    setToggling(true);
    try {
      await toggleOnline();
    } catch (error) {
      const code = error?.code;
      if (code === 'admin-dispatch-hold') {
        toast.error('Admin has paused new ride requests. Please contact support.');
      } else if (code === 'location-required') {
        toast.error(t('dash.goOnlineNeedsLocation'));
      } else if (code === 'location-fix-required') {
        toast.error(t('dash.goOnlineNeedsFix'));
      } else if (code === 'battery-optimization-required') {
        toast.error(t('dash.goOnlineNeedsBattery'), { duration: 6000 });
      } else if (code === 'location-publish-failed') {
        toast.error(t('dash.goOnlinePublishFailed'));
      } else if (code === 'native-auth-required') {
        toast.error(t('dash.goOnlineNeedsBackground'));
      } else {
        toast.error(t('authErrors.default'));
      }
    } finally { setToggling(false); }
  };

  const handleAccept = async () => {
    if (!driverActionsReady || !incoming || !driver) return;
    hapticSelection();
    stopContinuousAlert();
    clearPreviewRoute();
    setAccepting(true);
    await executeRideAccept({
      incoming,
      driver,
      isAcceptingRef: acceptingRef,
      acceptRideFn: acceptRide,
      onNavigate: (url, opts) => navigate(url, opts),
      onTaken: (rideId) => {
        toast.error(t('dash.rideTaken'));
        setDismissed((d) => ({ ...d, [offerKeyOf(rideId, incoming.offerRevision)]: true }));
        navigate('/', { replace: true });
      },
      onError: () => {
        toast.error(t('dash.acceptError'));
        navigate('/', { replace: true });
      },
    });
    setAccepting(false);
  };

  if (driver?.isBlocked) {
    return (
      <div className="app offline-screen">
        <h2>{t('dash.blockedTitle')}</h2>
        <p className="text-muted">{t('dash.blockedHint')}</p>
      </div>
    );
  }

  if (driver && !driver.isApproved && !treatApproved) {
    return (
      <div className="app offline-screen">
        <div className="offline-emoji">⏳</div>
        <h2>{t('dash.pendingTitle')}</h2>
        <p className="text-muted">{t('dash.pendingHint')}</p>
      </div>
    );
  }

  return (
    <div className="driver-screen">
      {treatApproved && !driver?.isBlocked && (
        <div ref={mapRef} className="driver-map" aria-hidden={!driver?.isOnline} />
      )}

      <div className="driver-top">
        <span className="driver-brand">
          <span className="driver-brand-icon" aria-hidden="true">🛺</span>
          <span className="driver-brand-text">
            <strong>MyTownly</strong>
            <small>{APP.city}</small>
          </span>
        </span>
        <LangToggle />
      </div>

      {!onlineNet && driverActionsReady && (
        <div className="net-banner">{t('dash.offlineNet')}</div>
      )}

      {!driverActionsReady && (
        <div className="session-refresh-banner">
          <span className="driver-bootstrap-spinner" aria-hidden="true" />
          {profileStatus === 'offline-cached'
            ? t('dash.reconnectToVerify')
            : t('dash.restoringSession')}
        </div>
      )}

      {driverActionsReady && driver?.isApproved && !driver?.isBlocked && (
        <div className="dash-batt-wrap">
          <BatteryOptimizationNotice t={t} />
        </div>
      )}

      {locationBlocked && liveOnline && (
        <button
          type="button"
          className="net-banner"
          style={{ background: '#b91c1c', border: 0, width: '100%', cursor: 'pointer' }}
          onClick={async () => {
            const refetch = () => getCurrentPosition({ maximumAge: 0, timeout: 20000 })
              .then((pos) => ensureSelfMarker(pos, { pan: true }))
              .catch(() => toast.error(t('dash.locationBlockedHint')));
            try {
              const state = await ensureLocationPermission({ request: true });
              if (state === 'denied') { showLocationDeniedToast(t); return; }
              refetch();
            } catch (e) {
              if (String(e?.message) === 'location_disabled') showLocationDisabledToast(t, refetch);
              else toast.error(t('dash.locationBlockedHint'));
            }
          }}
        >
          📍 {t('dash.locationBlocked')}
        </button>
      )}

      {driverActionsReady && user?.uid && liveOnline && !dispatchHeld && !incoming && (
        <div className="dash-prompt-wrap">
          <NotificationPrompt uid={user.uid} t={t} />
        </div>
      )}

      {!liveOnline ? (
        <div className="offline-panel">
          <div className="offline-emoji">🛺</div>
          <h2>{t('dash.offlineTitle')}</h2>
          <p className="text-muted">{dispatchHeld ? 'New ride requests are paused until the current trip ends.' : t('dash.offlineHint', { city: APP.city })}</p>
          <button type="button" className="btn btn-green btn-lg" disabled={toggling || !driverActionsReady || dispatchHeld} onClick={handleToggleOnline}>
            {driverActionsReady ? t('dash.goOnline') : t('dash.restoringSession')}
          </button>
        </div>
      ) : (
        <>
          <div className="online-pill">{t('dash.online')}</div>
          {dispatchHeld ? (
            <div className="waiting-banner" style={{ borderColor: 'rgba(245, 158, 11, 0.45)', color: '#b45309' }}>
              {String(driver?.adminDispatchHold?.state) === 'after_trip'
                ? 'Admin has paused new rides after this trip.'
                : 'Admin has paused new ride requests.'}
            </div>
          ) : !incoming && (
            <div className="waiting-banner">
              <div className="pulse-dot" />
              {t('dash.waiting')}
            </div>
          )}
        </>
      )}

      {incoming && liveOnline && !dispatchHeld && (
        <div className="ride-request-card">
          {accepting ? (
            // Instant, honest feedback the moment the tap lands — never claims
            // the ride is actually the driver's until executeRideAccept's own
            // server round trip confirms it. A ride goes out to several Annas
            // at once, so a near-simultaneous tap by someone else is a real,
            // regular case, not an edge case: this state must never look like
            // "accepted" before it truly is, or a losing Anna sees a false win.
            <div className="ride-request-confirming">
              <span className="ride-request-confirming-spinner" aria-hidden="true" />
              <strong>{t('dash.confirmingRide')}</strong>
              <span className="text-muted">{t('dash.confirmingRideHint')}</span>
            </div>
          ) : (
            <>
              <div className="ride-request-head">
                {t('dash.newRide')}
                <span className="accept-timer">{secondsLeft}s</span>
              </div>
              <div className="ride-request-fare">
                <span>₹{incoming.fare}</span>
                {Number(incoming.tip || incoming.fareTip || 0) > 0 && (
                  <span className="ride-request-tip-pill">
                    {t('dash.extraTipIncluded', { tip: incoming.tip || incoming.fareTip })}
                  </span>
                )}
              </div>
              <div className="ride-request-cash">{t('dash.cashCollect', { fare: incoming.fare })}</div>
              {Number(incoming.cancellationFee) > 0 && (
                <div className="ride-request-pastfee">
                  {t('dash.farePastFee', {
                    ride: incoming.fare - incoming.cancellationFee,
                    fee: incoming.cancellationFee,
                  })}
                </div>
              )}
              {(incoming.customerName || incoming.riderName) && (
                <div className="ride-request-rider">
                  <strong>
                    {incoming.isBookingForOther ? t('dash.rider') : t('dash.customer')}:{' '}
                    {incoming.customerName || incoming.riderName}
                  </strong>
                  {incoming.isBookingForOther && incoming.bookerName && (
                    <span className="text-muted"> {t('dash.bookedBy', { name: incoming.bookerName })}</span>
                  )}
                </div>
              )}
              <div className="ride-request-row">
                <span className="dot pickup" />
                <div>
                  <div className="ride-request-label">{t('dash.pickup')}</div>
                  <div className="ride-request-addr">{incoming.pickup?.address}</div>
                </div>
              </div>
              <div className="ride-request-row">
                <span className="dot drop" />
                <div>
                  <div className="ride-request-label">{t('dash.drop')}</div>
                  <div className="ride-request-addr">{incoming.destination?.name}</div>
                </div>
              </div>
              <div className="ride-request-meta">
                {incoming.distance} · {incoming.duration}
              </div>
              <div className="ride-request-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setDismissed((d) => ({ ...d, [incomingOfferKey]: true }))}>
                  {t('dash.ignore')}
                </button>
                <button
                  type="button"
                  className="btn btn-green"
                  disabled={!driverActionsReady}
                  onClick={handleAccept}
                >
                  {t('dash.accept')}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {liveOnline && !dispatchHeld && (
        <button type="button" className="offline-fab" disabled={toggling} onClick={handleToggleOnline}>
          {t('dash.goOffline')}
        </button>
      )}

      <IntertownTripCard />
      <OpenIntertownOffers />

      {driverActionsReady && showTour && <FirstTimeTour t={t} onDone={() => setShowTour(false)} />}

      <BottomNav />
    </div>
  );
}
